import { useCallback } from 'react';
import { ResponsiveLine, SliceTooltipProps } from '@nivo/line';
import { chartThemeDark } from '../../theme';

type ChartPoint = { x: number; y: number };

type ChartSeries = { id: string; color?: string; data: ChartPoint[] };

const MARGINS = {
  full: { top: 12, right: 16, bottom: 40, left: 48 },
  stacked: { top: 8, right: 16, bottom: 24, left: 48 },
} as const;

const ANIMATION = { mass: 1, tension: 170, friction: 26, clamp: false } as const;

function calcPaddedRange(data: { y: number }[]) {
  if (data.length === 0) return { min: 0, max: 1 };
  const values = data.map(p => p.y);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  const paddingPct = range < 1 ? 0.2 : range < 10 ? 0.15 : 0.08;
  const padding = Math.max(range * paddingPct, 0.1);
  return {
    min: Math.floor((min - padding) * 100) / 100,
    max: Math.ceil((max + padding) * 100) / 100,
  };
}

function formatY(valueRange: { min: number; max: number }) {
  return (value: number) => {
    const range = valueRange.max - valueRange.min;
    if (range < 1) return value.toFixed(1);
    if (range < 10) return value.toFixed(1);
    return value.toFixed(0);
  };
}

function formatSeconds(s: number): string {
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s % 60);
  return rem ? `${m}m ${rem}s` : `${m}m`;
}

export interface InsightsLineChartProps {
  /** Nivo series. Each serie: { id, color, data: [{x: number (elapsed seconds), y: number}] } */
  data: ChartSeries[];
  height: number;
  /** If omitted, computed from data. */
  valueRange?: { min: number; max: number };
  /** Elapsed-time range in seconds. Used to set xScale min/max. */
  timeRange?: { start: number; end: number };
  /** When false, X-axis ticks are hidden (stacked non-bottom chart). */
  showXAxis?: boolean;
  /** Color for the single series line. Ignored when data has multiple series. */
  seriesColor?: string;
  /** Metric display name shown in the tooltip (e.g. "Heart Rate"). */
  tooltipLabel?: string;
  /** Unit appended to the value in the tooltip (e.g. "BPM"). */
  tooltipUnit?: string;
}

export function InsightsLineChart({
  data,
  height,
  valueRange: valueRangeProp,
  timeRange,
  showXAxis = true,
  seriesColor,
  tooltipLabel,
  tooltipUnit,
}: InsightsLineChartProps) {
  const allPoints = data.flatMap(s => s.data as ChartPoint[]);
  const valueRange = valueRangeProp ?? calcPaddedRange(allPoints);
  const margins = showXAxis ? MARGINS.full : MARGINS.stacked;

  const renderTooltip = useCallback(
    ({ slice }: SliceTooltipProps<ChartSeries>) => {
      const firstPoint = slice.points[0];
      if (!firstPoint) return null;
      const elapsed = typeof firstPoint.data.x === 'number' ? firstPoint.data.x : 0;
      return (
        <div className="min-w-[160px] rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface)] px-3 py-2.5 text-xs shadow-xl">
          <p className="mb-2 font-medium text-[var(--anl-text-muted)]">{formatSeconds(elapsed)}</p>
          {slice.points.map(point => {
            const label =
              slice.points.length === 1 && tooltipLabel ? tooltipLabel : point.seriesId;
            const value = formatY(valueRange)(point.data.y as number);
            return (
              <div key={point.id} className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-1.5 text-[var(--anl-text-muted)]">
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: point.seriesColor }}
                  />
                  <span>{label}</span>
                </div>
                <span className="tabular-nums font-semibold" style={{ color: point.seriesColor }}>
                  {value}
                  {tooltipUnit && (
                    <span className="ml-0.5 font-normal opacity-70">{tooltipUnit}</span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      );
    },
    [valueRange, tooltipLabel, tooltipUnit],
  );

  return (
    <div style={{ height }}>
      <ResponsiveLine
        data={data}
        margin={margins}
        xScale={{
          type: 'linear',
          min: timeRange?.start ?? 'auto',
          max: timeRange?.end ?? 'auto',
        }}
        yScale={{ type: 'linear', min: valueRange.min, max: valueRange.max, stacked: false }}
        curve="linear"
        axisTop={null}
        axisRight={null}
        axisBottom={
          showXAxis
            ? {
                format: v => formatSeconds(v as number),
                tickSize: 4,
                tickPadding: 4,
                tickRotation: -20,
                tickValues: 6,
              }
            : null
        }
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          tickValues: 5,
          format: formatY(valueRange),
        }}
        colors={seriesColor ? [seriesColor] : { datum: 'color' }}
        lineWidth={2.5}
        pointSize={4}
        pointBorderWidth={1}
        pointBorderColor={{ from: 'seriesColor', modifiers: [['darker', 0.3]] }}
        enableGridX={false}
        enableGridY={true}
        gridYValues={5}
        defs={[
          {
            id: 'areaGradient',
            type: 'linearGradient',
            colors: [
              { offset: 0, color: 'inherit', opacity: 0.25 },
              { offset: 100, color: 'inherit', opacity: 0 },
            ],
          },
        ]}
        fill={[{ match: '*', id: 'areaGradient' }]}
        enableArea={true}
        areaBaselineValue={valueRange.min}
        enableSlices="x"
        useMesh={true}
        sliceTooltip={renderTooltip}
        theme={chartThemeDark}
        animate={true}
        motionConfig={ANIMATION}
      />
    </div>
  );
}
